// @mui
import { styled } from '@mui/material/styles';
import { Stack, Container, Typography, Card, Box } from '@mui/material';
import Iconify from 'src/components/iconify';
import { useLocales } from 'src/locales';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  padding: theme.spacing(8, 0),
  [theme.breakpoints.up('md')]: {
    paddingTop: theme.spacing(15),
    paddingBottom: theme.spacing(0),
  },
}));

// ----------------------------------------------------------------------

interface ServicesData {
  name: string;
  icon: string;
  description: string;
}

export default function ServicesSection() {
  const { t } = useLocales();
  const servicesData: ServicesData[] = t('servicesData', { returnObjects: true });

  return (
    <RootStyle id="services">
      <Container>
        <Stack
          sx={{
            maxWidth: 480,
            mb: { xs: 8, md: 5 },
            mx: { xs: 'auto', md: 'unset' },
            textAlign: { xs: 'center', md: 'unset' },
          }}
        >
          <Typography variant="overline" sx={{ color: 'text.disabled' }}>
            {t('servicesSection.ourServices')}
          </Typography>

          <Typography variant="h2" sx={{ mt: 2, mb: 3 }}>
            {t('servicesSection.weProvide')}
          </Typography>

          <Typography sx={{ color: 'text.secondary' }}>
            {t('servicesSection.worldClassSolutions')}
          </Typography>
        </Stack>

        <Box
          sx={{
            alignItems: 'center',
            display: 'grid',
            gap: 4,
            gridTemplateColumns: {
              xs: 'repeat(1, 1fr)',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)',
            },
          }}
        >
          {servicesData.map((service, index) => (
            <ServiceItem key={service.name} service={service} index={index} />
          ))}
        </Box>
      </Container>
    </RootStyle>
  );
}

// ----------------------------------------------------------------------

type ServiceItemProps = {
  service: ServicesData;
  index: number;
};

function ServiceItem({ service, index }: ServiceItemProps) {
  const { name, description } = service;

  return (
    <Card
      sx={{
        px: 2,
        py: 2,
        textAlign: 'initial',

        ...(index === 0 && {
          boxShadow: (theme) => ({ md: theme.customShadows.z8 }),
        }),
        ...(index === 1 && {
          py: { xs: 5, md: 8 },
        }),
        ...(index === 2 && {
          py: { xs: 5, md: 10 },
          boxShadow: (theme) => ({ md: theme.customShadows.z24 }),
        }),
        ...(index === 3 && {
          boxShadow: (theme) => ({ md: theme.customShadows.z4 }),
        }),
      }}
    >
      <Stack alignItems="center" justifyContent="center">
        <Stack
          component={Box}
          width={70}
          height={70}
          justifyContent="center"
          alignItems="center"
          bgcolor="white"
          color="secondary.darker"
          sx={{
            border: (theme) => `3px solid ${theme.palette.primary.main}`,
          }}
          borderRadius={100}
        >
          <Iconify icon={service.icon} width={50} height={50} />
        </Stack>
      </Stack>

      <Stack spacing={1} sx={{ my: 5 }}>
        <Typography variant="h6">{name}</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {description}
        </Typography>
      </Stack>
    </Card>
  );
}
